import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useCallkitDataKeys, setCallUIState } from '../model';
import { attendCall, rejectCall } from '../services/callService';
import { CALLKIT_CALLSTATUS, CALL_TYPE } from '../constants';
import { useLanguage } from '@/localization/useLanguage';
import { getLocalUserName } from '../utils/helper';

// Status key map extracted to constant — no logic in UI
const STATUS_KEY_MAP = {
   [CALLKIT_CALLSTATUS?.RINGING]: 'RINGING',
   [CALLKIT_CALLSTATUS?.CONNECTING]: 'CONNECTING',
   [CALLKIT_CALLSTATUS?.CONNECTED]: 'CONNECTED',
   [CALLKIT_CALLSTATUS?.DISCONNECTED]: 'DISCONNECTED',
   [CALLKIT_CALLSTATUS?.REJECTED]: 'BUSY',
   [CALLKIT_CALLSTATUS?.ENGAGED]: 'ENGAGED',
   [CALLKIT_CALLSTATUS?.TIMEOUT]: 'TIME_OUT',
   [CALLKIT_CALLSTATUS?.CALLING]: 'CALLING',
   [CALLKIT_CALLSTATUS?.TRY_TO_CONNECT]: 'TRYING_TO_CONNECT',
   [CALLKIT_CALLSTATUS?.RECONNECTING]: 'RECONNECTING',
};

/**
 * ViewModel for CallConnectionScreen.
 * Owns: call status derivation, status text, audio playback, minimize action, attend/decline.
 */
export const useCallConnectionScreen = () => {
   const dispatch = useDispatch();
   const { t } = useLanguage();

   const { callScreenStatus, makeCallDetails, incomingCallDetails, callType, ongoingCallParticipants, groupId } =
      useCallkitDataKeys([
         'callScreenStatus',
         'makeCallDetails',
         'incomingCallDetails',
         'callType',
         'ongoingCallParticipants',
         'groupId',
      ]);

   const remoteUserId = makeCallDetails?.calleeId?.[0] || incomingCallDetails?.callerId;
   const groupName = makeCallDetails?.groupName ?? null;

   // ── Derived call status ──────────────────────────────────────────────────────
   const callStatus = (() => {
      const currentUserId = getLocalUserName();
      const remoteParticipants = (ongoingCallParticipants || []).filter(p => p.userId !== currentUserId);

      if (groupId || remoteParticipants.length > 1) {
         if (remoteParticipants.some(p => p.callStatus === CALLKIT_CALLSTATUS.CONNECTED))
            return CALLKIT_CALLSTATUS.CONNECTED;
         if (remoteParticipants.some(p => p.callStatus === CALLKIT_CALLSTATUS.RINGING))
            return CALLKIT_CALLSTATUS.RINGING;
         if (remoteParticipants.some(p => p.callStatus === CALLKIT_CALLSTATUS.CALLING))
            return CALLKIT_CALLSTATUS.CALLING;
         return remoteParticipants[0]?.callStatus || CALLKIT_CALLSTATUS.TRY_TO_CONNECT;
      }
      const remoteUser = ongoingCallParticipants?.find(p => p.userId === remoteUserId);
      return remoteUser?.callStatus || 'Try to connect';
   })();

   // ── Status text ────────────────────────────────────────────────────────────
   const statusDisplay = (() => {
      if (incomingCallDetails) {
         const isVideo = callType === CALL_TYPE.VIDEO;
         if (groupId) return isVideo ? t('common.INCOMING_GROUP_VIDEO_CALL') : t('common.INCOMING_GROUP_AUDIO_CALL');
         return isVideo ? t('common.INCOMING_VIDEO_CALL') : t('common.INCOMING_AUDIO_CALL');
      }
      const key = STATUS_KEY_MAP[callStatus];
      return key ? t(`calls.STATUS.${key}`) : callStatus;
   })();

   // ── Audio/Video Mute status ───────────────────────────────────────────
   const isAudioMuted = (() => {
      const currentUserId = getLocalUserName();
      const localParticipant = (ongoingCallParticipants || []).find(p => p.userId === currentUserId);
      if (localParticipant) return localParticipant.isAudioMuted;
      return !makeCallDetails?.isMicrophoneEnabled || false;
   })();

   const isVideoMuted = (() => {
      const currentUserId = getLocalUserName();
      const localParticipant = (ongoingCallParticipants || []).find(p => p.userId === currentUserId);
      if (localParticipant) return localParticipant.isVideoMuted;
      return !makeCallDetails?.isVideoCall || false;
   })();

   // ── Handlers ─────────────────────────────────────────────────────────────
   const handleBack = useCallback(() => {
      dispatch(setCallUIState('minimized'));
   }, [dispatch]);

   const handleAttend = useCallback(() => attendCall(), []);
   const handleDecline = useCallback(() => rejectCall(), []);

   return {
      callScreenStatus,
      remoteUserId,
      groupId,
      groupName,
      callType,
      ongoingCallParticipants,
      callStatus,
      statusDisplay,
      isAudioMuted,
      isVideoMuted,
      incomingCallDetails,
      localUserId: getLocalUserName(),
      handleBack,
      handleAttend,
      handleDecline,
   };
};
